import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  MapPin,
  Plus,
  Pencil,
  Trash2,
  Star,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Home,
  Briefcase,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { useAuth } from '../../hooks/useAuth';
import { cn } from '../../lib/utils';
import { db } from '../../config/firebase';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';

interface Address {
  id: string;
  label: string;
  street: string;
  apt?: string;
  city: string;
  state: string;
  zip: string;
  isDefault: boolean;
}

type ToastState = { message: string; type: 'success' | 'error' } | null;

function Toast({ toast, onClose }: { toast: ToastState; onClose: () => void }) {
  if (!toast) return null;
  return (
    <div className={cn(
      'fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center space-x-2 px-5 py-3 rounded-xl shadow-lg text-sm font-medium max-w-sm',
      toast.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
    )}>
      {toast.type === 'success' ? <CheckCircle2 className="h-4 w-4 flex-shrink-0" /> : <AlertCircle className="h-4 w-4 flex-shrink-0" />}
      <span>{toast.message}</span>
      <button onClick={onClose} className="ml-2 text-current opacity-50 hover:opacity-100">&times;</button>
    </div>
  );
}

const LABEL_OPTIONS = [
  { value: 'Home', icon: Home },
  { value: 'Work', icon: Briefcase },
  { value: 'Other', icon: MapPin },
];

const EMPTY_FORM = { label: 'Home', street: '', apt: '', city: '', state: '', zip: '' };

export function AddressesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastState>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'users', user.uid, 'addresses'), orderBy('isDefault', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() } as Address));
      setAddresses(data);
      setLoading(false);
    }, () => {
      setLoading(false);
    });
    return unsub;
  }, [user]);

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (addr: Address) => {
    setEditingId(addr.id);
    setForm({
      label: addr.label,
      street: addr.street,
      apt: addr.apt || '',
      city: addr.city,
      state: addr.state,
      zip: addr.zip,
    });
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    try {
      setSaving(true);
      const data = {
        label: form.label,
        street: form.street.trim(),
        apt: form.apt.trim() || null,
        city: form.city.trim(),
        state: form.state.trim(),
        zip: form.zip.trim(),
        isDefault: addresses.length === 0,
        updatedAt: serverTimestamp(),
      };
      if (editingId) {
        await updateDoc(doc(db, 'users', user.uid, 'addresses', editingId), data);
        showToast('Address updated', 'success');
      } else {
        await addDoc(collection(db, 'users', user.uid, 'addresses'), {
          ...data,
          createdAt: serverTimestamp(),
        });
        showToast('Address added', 'success');
      }
      setShowForm(false);
      setForm(EMPTY_FORM);
      setEditingId(null);
    } catch (err: any) {
      showToast(err.message || 'Failed to save address', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!user) return;
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'addresses', id));
      showToast('Address removed', 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to delete address', 'error');
    }
  };

  const handleSetDefault = async (id: string) => {
    if (!user) return;
    try {
      for (const addr of addresses) {
        if (addr.isDefault) {
          await updateDoc(doc(db, 'users', user.uid, 'addresses', addr.id), { isDefault: false });
        }
      }
      await updateDoc(doc(db, 'users', user.uid, 'addresses', id), { isDefault: true });
      showToast('Default address updated', 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to set default', 'error');
    }
  };

  const getLabelIcon = (label: string) => {
    const opt = LABEL_OPTIONS.find(o => o.value === label);
    return opt?.icon || MapPin;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toast toast={toast} onClose={() => setToast(null)} />

      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button onClick={() => navigate('/settings')} className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors">
                <ArrowLeft className="h-6 w-6 text-heading" />
              </button>
              <h1 className="ml-4 text-xl font-serif text-heading">Addresses</h1>
            </div>
            {!showForm && (
              <Button size="sm" onClick={openAdd}>
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4 pb-24">
        {/* Form */}
        {showForm && (
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-medium text-heading">{editingId ? 'Edit Address' : 'New Address'}</h2>
                <button onClick={() => { setShowForm(false); setEditingId(null); }} className="p-1 hover:bg-gray-100 rounded-full">
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
              <form onSubmit={handleSave} className="space-y-3">
                <div className="flex space-x-2">
                  {LABEL_OPTIONS.map(opt => {
                    const Icon = opt.icon;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setForm(f => ({ ...f, label: opt.value }))}
                        className={cn(
                          'flex items-center space-x-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors',
                          form.label === opt.value
                            ? 'bg-primary/10 border-primary text-primary'
                            : 'bg-white border-gray-200 text-body hover:bg-gray-50'
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        <span>{opt.value}</span>
                      </button>
                    );
                  })}
                </div>
                <div>
                  <label className="block text-sm font-medium text-heading mb-1">Street Address</label>
                  <Input value={form.street} onChange={e => setForm(f => ({ ...f, street: e.target.value }))} placeholder="123 Main St" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-heading mb-1">Apt / Suite (optional)</label>
                  <Input value={form.apt} onChange={e => setForm(f => ({ ...f, apt: e.target.value }))} placeholder="Apt 4B" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-heading mb-1">City</label>
                    <Input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="New York" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-heading mb-1">State</label>
                    <Input value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} placeholder="NY" required maxLength={2} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-heading mb-1">ZIP</label>
                    <Input value={form.zip} onChange={e => setForm(f => ({ ...f, zip: e.target.value }))} placeholder="10001" required maxLength={10} />
                  </div>
                </div>
                <Button type="submit" loading={saving} className="w-full">
                  {editingId ? 'Update Address' : 'Save Address'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
            <p className="mt-3 text-body text-sm">Loading addresses...</p>
          </div>
        )}

        {/* Empty */}
        {!loading && addresses.length === 0 && !showForm && (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-purple-50 flex items-center justify-center mx-auto mb-4">
              <MapPin className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-lg font-medium text-heading mb-2">No saved addresses</h2>
            <p className="text-sm text-body mb-6">Add an address for faster checkout</p>
            <Button onClick={openAdd}>
              <Plus className="h-4 w-4 mr-2" /> Add Address
            </Button>
          </div>
        )}

        {/* List */}
        {!loading && addresses.map(addr => {
          const Icon = getLabelIcon(addr.label);
          return (
            <Card key={addr.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center mt-0.5',
                      addr.isDefault ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-500'
                    )}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <p className="font-medium text-heading">{addr.label}</p>
                        {addr.isDefault && (
                          <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">Default</span>
                        )}
                      </div>
                      <p className="text-sm text-body mt-0.5">
                        {addr.street}{addr.apt ? `, ${addr.apt}` : ''}
                      </p>
                      <p className="text-sm text-body">
                        {addr.city}, {addr.state} {addr.zip}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    {!addr.isDefault && (
                      <button
                        onClick={() => handleSetDefault(addr.id)}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        title="Set as default"
                      >
                        <Star className="h-4 w-4 text-gray-400" />
                      </button>
                    )}
                    <button
                      onClick={() => openEdit(addr)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <Pencil className="h-4 w-4 text-gray-400" />
                    </button>
                    <button
                      onClick={() => handleDelete(addr.id)}
                      className="p-2 hover:bg-red-50 rounded-full transition-colors"
                    >
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </main>
    </div>
  );
}
